import { useState, useEffect } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Star, StarIcon, Trash2, Globe, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/hooks/useLanguage";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

interface CharacterPreviewProps {
  character: {
    id?: string;
    name: string;
    description: string;
    avatar_url?: string;
    role: string;
    personality_traits: string[];
    strengths: string[];
    weaknesses: string[];
    skills: string[];
    translation_overrides?: Record<string, any>;
    is_public?: boolean;
    average_rating?: number | null;
    author?: string;
    user_id?: string;
  };
  showRating?: boolean;
  enableNavigation?: boolean;
  onDelete?: (id: string) => void;
}

const CharacterPreview = ({ 
  character, 
  showRating = true,
  enableNavigation = false,
  onDelete
}: CharacterPreviewProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [userRating, setUserRating] = useState<number | null>(null);
  const [isRating, setIsRating] = useState(false);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    const checkOwnership = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsOwner(user?.id === character.user_id);
    };
    checkOwnership();
  }, [character.user_id]);

  const getTranslatedText = (key: string, value: string) => {
    if (character.translation_overrides?.[key]) {
      return character.translation_overrides[key];
    }

    if (key === 'role') {
      const translationKey = `dashboard.filters.roles.${value}`;
      const roleTranslation = t(translationKey);
      return roleTranslation !== translationKey ? roleTranslation : value;
    }

    return value;
  };

  const loadRatings = async () => {
    if (!character.id) return;

    // Create an "aborted" flag that we can use for cleanup
    let aborted = false;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || aborted) return;

      // First check if character still exists to avoid 406 errors
      const { data: charExists, error: checkError } = await supabase
        .from('characters')
        .select('id')
        .eq('id', character.id)
        .maybeSingle();
      
      if (checkError || !charExists || aborted) {
        logger.log('Character no longer exists or cannot be accessed');
        return;
      }

      // Load user's rating
      const { data: ratingData, error: ratingError } = await supabase
        .from('character_ratings')
        .select('rating')
        .eq('character_id', character.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!ratingError && ratingData && !aborted) {
        setUserRating(ratingData.rating);
      }

      // Load average rating
      const { data: charData, error: charError } = await supabase
        .from('characters')
        .select('average_rating')
        .eq('id', character.id)
        .maybeSingle();  // Use maybeSingle instead of single to handle not found case gracefully

      if (!charError && charData?.average_rating !== null && 
          charData?.average_rating !== undefined && !aborted) {
        setAverageRating(Number(charData.average_rating));
      }
    } catch (error) {
      logger.error('Error loading ratings:', error);
    }
    
    // Return a cleanup function
    return () => {
      aborted = true;
    };
  };

  useEffect(() => {
    let cleanupFn: (() => void) | undefined;
    
    if (showRating && character.id) {
      // Start loading the ratings
      const promise = loadRatings();
      
      // Return a cleanup function that will be called when the component unmounts
      return () => {
        // When this effect is cleaned up, set aborted to true
        promise.then(cleanup => {
          if (cleanup) cleanup();
        });
      };
    }
    
    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [character.id, showRating]);

  const handleRate = async (rating: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the click from bubbling up to the card
    if (isRating || !character.id) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: t("dashboard.characterManagement.rating.error"),
        description: "You must be logged in to rate characters",
      });
      return;
    }

    setIsRating(true);
    logger.log('Submitting rating:', { userId: user.id, characterId: character.id, rating });
    
    try {
      const { error } = await supabase
        .from('character_ratings')
        .upsert(
          {
            character_id: character.id,
            rating,
            user_id: user.id
          },
          {
            onConflict: 'character_id,user_id'
          }
        );

      if (error) throw error;

      // Wait a moment for the trigger to update the average
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch the updated average rating
      const { data: updatedChar, error: charError } = await supabase
        .from('characters')
        .select('average_rating')
        .eq('id', character.id)
        .single();

      if (charError) throw charError;

      logger.log('Updated character data:', updatedChar);

      // Update local state
      setUserRating(rating);
      if (updatedChar?.average_rating !== null && updatedChar?.average_rating !== undefined) {
        logger.log('Setting new average rating:', updatedChar.average_rating);
        setAverageRating(Number(updatedChar.average_rating));
      }

      toast({
        title: t("dashboard.characterManagement.rating.submitted"),
        description: t("dashboard.characterManagement.rating.thankYou"),
      });
    } catch (error) {
      logger.error('Error rating character:', error);
      toast({
        variant: "destructive",
        title: t("dashboard.characterManagement.rating.error"),
        description: t("dashboard.characterManagement.rating.failed"),
      });
    } finally {
      setIsRating(false);
    }
  };

  const handleDelete = async () => {
    if (!character.id || isDeleting) return;
    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('characters')
        .delete()
        .eq('id', character.id);

      if (error) throw error;

      toast({
        title: t("dashboard.characterManagement.delete.success.title"),
        description: t("dashboard.characterManagement.delete.success.description"),
      });

      // Invalidate all queries that could reference this character
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      queryClient.removeQueries({ queryKey: ["character", character.id] });
      
      // Reset local state to prevent further API calls for this character
      setAverageRating(null);
      setUserRating(null);

      // Call onDelete immediately to either navigate away or refresh the list
      if (onDelete) {
        onDelete(character.id);
      }
    } catch (error) {
      logger.error('Error deleting character:', error);
      toast({
        variant: "destructive",
        title: t("dashboard.characterManagement.delete.error.title"),
        description: t("dashboard.characterManagement.delete.error.description"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const togglePublic = async () => {
    if (!character.id || isTogglingPublic) return;
    setIsTogglingPublic(true);

    try {
      const { error } = await supabase
        .from('characters')
        .update({ is_public: !character.is_public })
        .eq('id', character.id);

      if (error) throw error;

      toast({
        title: character.is_public 
          ? t("dashboard.characterManagement.visibility.setPrivate") 
          : t("dashboard.characterManagement.visibility.setPublic"),
        description: character.is_public 
          ? t("dashboard.characterManagement.visibility.nowPrivate")
          : t("dashboard.characterManagement.visibility.nowPublic"),
      });

      // Update the local state
      character.is_public = !character.is_public;
    } catch (error) {
      logger.error('Error toggling character visibility:', error);
      toast({
        variant: "destructive",
        title: t("dashboard.characterManagement.visibility.error"),
        description: t("dashboard.characterManagement.visibility.updateFailed"),
      });
    } finally {
      setIsTogglingPublic(false);
    }
  };

  const handleCardClick = () => {
    if (enableNavigation && character.id) {
      navigate(`/characters/${character.id}`);
    }
  };

  return (
    <Card 
      className={`bg-gray-800 border-gray-700 hover:bg-gray-700/80 transition-colors ${enableNavigation ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <CardHeader className="flex flex-row items-start gap-2"> {/* Reduced gap */}
        <Avatar className="w-12 h-12"> {/* Reduced size */}
          {character.avatar_url ? (
            <AvatarImage src={character.avatar_url} alt={character.name} />
          ) : (
            <AvatarFallback className="bg-purple-600 text-white">
              {character.name.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white text-sm leading-tight p-2"> {/* Smaller font */}
                {character.name || t('dashboard.createCharacter.form.unnamedCharacter')}
              </CardTitle>
              {character.is_public && character.author && (
                <p className="text-xs text-gray-300 leading-tight"> {/* Smaller font */}
                  by {character.author}
                </p>
              )}
            </div>
            {isOwner && (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}> {/* Reduced gap */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePublic}
                  disabled={isTogglingPublic}
                  className="h-6 w-6" 
                >
                  {character.is_public ? (
                    <Globe className="h-4 w-4 text-green-400" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 hover:bg-red-500/20 hover:text-red-500" 
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("dashboard.characterManagement.delete.title")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("dashboard.characterManagement.delete.confirmMessage")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("dashboard.characterManagement.delete.cancel")}</AlertDialogCancel>
                      <Button 
                        variant="destructive" 
                        onClick={handleDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting 
                          ? t("dashboard.characterManagement.delete.deleting")
                          : t("dashboard.characterManagement.delete.delete")
                        }
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
          {character.role && (
            <p className="text-xs text-gray-300 mt-1 leading-tight"> {/* Smaller font and reduced margin */}
              {getTranslatedText('role', character.role)}
            </p>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2"> {/* Reduced spacing */}
        {character.description && (
          <p className="text-sm text-gray-300 leading-tight">{character.description}</p> 
        )}

        {character.personality_traits.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1 text-white"> 
              {t('dashboard.characterCard.personality')}
            </h4>
            <div className="flex flex-wrap gap-1"> 
              {character.personality_traits.map((trait) => (
                <span
                  key={trait}
                  className="text-xs bg-purple-900/50 px-1 py-0.5 rounded text-purple-100 border border-purple-700" 
                >
                  {getTranslatedText('personality_traits', trait)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2"> 
          {character.strengths.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold mb-1 text-white"> {/* Smaller font and reduced margin */}
                {t('dashboard.characterCard.strengths')}
              </h4>
              <ul className="text-xs space-y-1"> 
                {character.strengths.map((strength) => (
                  <li key={strength} className="text-gray-300">
                    {getTranslatedText('strengths', strength)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {character.weaknesses.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold mb-1 text-white"> {/* Smaller font and reduced margin */}
                {t('dashboard.characterCard.weaknesses')}
              </h4>
              <ul className="text-xs space-y-1"> {/* Smaller font and reduced spacing */}
                {character.weaknesses.map((weakness) => (
                  <li key={weakness} className="text-gray-300">
                    {getTranslatedText('weaknesses', weakness)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {character.skills.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1 text-white"> 
              {t('dashboard.characterCard.skills')}
            </h4>
            <div className="flex flex-wrap gap-1">
              {character.skills.map((skill) => (
                <span
                  key={skill}
                  className="text-xs bg-purple-900/50 px-1 py-0.5 rounded text-purple-100 border border-purple-700" 
                >
                  {getTranslatedText('skills', skill)}
                </span>
              ))}
            </div>
          </div>
        )}

        {showRating && (
          <div className="flex items-center justify-center gap-1 bg-gray-700/50 p-2 rounded-lg mt-2" onClick={(e) => e.stopPropagation()}> {/* Reduced gap, padding, and margin */}
            {[1, 2, 3, 4, 5].map((star) => (
              <Button
                key={star}
                variant="ghost"
                size="sm"
                onClick={(e) => handleRate(star, e)}
                disabled={isRating}
                className="p-0.5 hover:scale-105 transition-transform"
              >
                {star <= (userRating || 0) ? (
                  <StarIcon className="h-4 w-4 text-yellow-400 fill-yellow-400" /> 
                ) : (
                  <Star className="h-4 w-4 text-gray-400" /> 
                )}
              </Button>
            ))}
            {averageRating !== null && (
              <span className="text-xs text-gray-400 ml-1">
                ({Number(averageRating).toFixed(1)})
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CharacterPreview;
